/**
 * Centralised pricing helpers.
 *
 * Products now have two prices:
 *   - priceLkr        - manual/admin topup price
 *   - websitePriceLkr - website/online price (set by admin, already includes
 *                       the payment gateway margin - no extra fee added on top)
 *
 * The 2.8% surcharge is no longer added in the frontend. The website price
 * shown to customers is exactly what is charged via the gateway.
 */

export interface CartItem {
  qty: number;
  isChannelItem?: boolean;
  item_id?: number;
  region?: string;
  priceLkr?: number;
  websitePriceLkr?: number;
}

export interface Product {
  id: string;
  priceLkr?: number;
  websitePriceLkr?: number;
  [key: string]: any;
}

/**
 * Get the effective display price for a product on the website.
 * Falls back to priceLkr if websitePriceLkr is not set.
 */
export function getWebsitePrice(product: Product, activeOffers?: Record<string, any>): number {
  const offer = activeOffers?.[product.id];
  if (offer?.discountedPrice !== undefined) {
    return offer.discountedPrice;
  }
  return product.websitePriceLkr ?? product.priceLkr ?? 0;
}

/** Calculate the raw subtotal (LKR) for items.
 *  Uses website price (already inclusive of gateway margin). */
export function calcSubtotal(
  cart: Record<string, CartItem>,
  products: Product[],
  activeOffers?: Record<string, any>,
): number {
  return Object.entries(cart).reduce((acc, [id, item]) => {
    // Standard catalog lookup
    const product = products.find((p) => p.id === id);
    if (product) {
      return acc + getWebsitePrice(product, activeOffers) * item.qty;
    }
    
    // Fallback logic for dynamic injected mock items (e.g. channel items)
    if (item.isChannelItem) {
      const priceLkr = (item as any).priceLkr || (item as any).websitePriceLkr || 0;
      if (priceLkr > 0) {
        return acc + priceLkr * item.qty;
      }
    }
    
    return acc;
  }, 0);
}

/** Calculate how much referral balance will actually be deducted. */
export function calcReferralDiscount(
  subtotal: number,
  referralBalance: number,
  useReferralBalance: boolean,
): number {
  if (!useReferralBalance) return 0;
  return Math.min(referralBalance, subtotal);
}

/**
 * @deprecated Website price is now all-in. Kept for backward compatibility.
 */
export function calcGenieTotal(baseAmount: number, feeRate: number): number {
  if (feeRate >= 1) return baseAmount;
  return Number((baseAmount * (1 + feeRate)).toFixed(2));
}

/**
 * @deprecated No longer used for public order pricing. Kept for backward compatibility.
 */
export function getGenieFeeRate(paymentConfig: any): number {
  const customRate = paymentConfig?.gateways?.find((g: any) => g.id === 'GENIE')?.feeRate;
  return customRate !== undefined ? customRate : 0.028;
}
